import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useDeviceSafeArea } from '../../hooks/useDeviceSafeArea';
import { Logo } from './Logo';
import { User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileModal } from './ProfileModal';

export const TopBar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const safeAreaTop = useDeviceSafeArea();
  const { isAuthenticated } = useAuth();

  // Check for scrollable content on the current page
  const checkScrollableContent = () => {
    // Check if page itself is scrollable
    const isPageScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight + 100;
    
    // Check if any container has meaningful scrollable content
    const scrollableContainers = document.querySelectorAll('[class*="overflow-y-auto"], [class*="overflow-y-scroll"], [style*="overflow-y: auto"], [style*="overflow-y: scroll"]');
    let hasScrollableContent = false;
    
    scrollableContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        if (scrollHeight > clientHeight + 50) {
          hasScrollableContent = true;
        }
      }
    });
    
    return isPageScrollable || hasScrollableContent;
  };

  // Reset blur state when navigating to a new page, but check for scrollable content
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Check if the new page has scrollable content
    const hasContent = checkScrollableContent();
    
    if (location.pathname.includes('/visualization')) {
      setIsScrolled(true);
      
      setTimeout(() => {
        const stillHasContent = checkScrollableContent();
        if (!stillHasContent) {
          setIsScrolled(false);
        }
      }, 100);
    } else {
      setIsScrolled(hasContent);
    }
  }, [location.pathname]);

  useEffect(() => {
    const activateBlur = () => {
      setIsScrolled(true);
      
      // Reset blur after a delay if no more scrolling
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        // Only hide blur if there's no scrollable content
        if (!checkScrollableContent()) {
          setIsScrolled(false);
        }
      }, 3000); // Increased from 1500ms to 3000ms
    };

    const handleScroll = () => {
      activateBlur();
    };

    const handleWheel = (e: WheelEvent) => {
      // Only activate if there's actual scrolling (deltaY !== 0)
      if (Math.abs(e.deltaY) > 0) {
        activateBlur();
      }
    };

    const handleTouchStart = () => {
      // Touch events might indicate scrolling
      activateBlur();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Arrow keys, Page Up/Down, Home/End for scrolling
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (scrollKeys.includes(e.key)) {
        activateBlur();
      }
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    
    // Also listen for scroll events on scrollable containers
    const scrollableContainers = document.querySelectorAll('.scrollable-content, [class*="overflow-y-auto"], [class*="overflow-y-scroll"], [style*="overflow-y: auto"], [style*="overflow-y: scroll"]');
    
    scrollableContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.addEventListener('scroll', handleScroll, { passive: true });
        container.addEventListener('wheel', handleWheel, { passive: true });
      }
    });

    // Use ResizeObserver to detect new scrollable containers
    const resizeObserver = new ResizeObserver(() => {
      // Check for new scrollable containers and add listeners
      const newScrollableContainers = document.querySelectorAll('[class*="overflow-y-auto"], [class*="overflow-y-scroll"], [style*="overflow-y: auto"], [style*="overflow-y: scroll"]');
      
      newScrollableContainers.forEach(container => {
        if (container instanceof HTMLElement && !container.hasAttribute('data-scroll-listener-added')) {
          container.setAttribute('data-scroll-listener-added', 'true');
          container.addEventListener('scroll', handleScroll, { passive: true });
          container.addEventListener('wheel', handleWheel, { passive: true });
        }
      });
      
      // Check if content became scrollable
      if (checkScrollableContent()) {
        setIsScrolled(true);
      }
    });

    // Observe body and main content areas
    resizeObserver.observe(document.body);
    const mainContent = document.querySelector('main') || document.querySelector('#root') || document.body;
    resizeObserver.observe(mainContent);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('keydown', handleKeyDown);
      
      // Remove listeners from containers
      scrollableContainers.forEach(container => {
        if (container instanceof HTMLElement) {
          container.removeEventListener('scroll', handleScroll);
          container.removeEventListener('wheel', handleWheel);
        }
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div 
      className={`md:hidden fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'backdrop-blur-md bg-black/20' 
          : ''
      }`}
      style={{ paddingTop: safeAreaTop > 0 ? `${safeAreaTop}px` : '0px' }}
      data-web-mode={safeAreaTop === 0 ? 'true' : undefined}
    >
      <div className="flex justify-between items-center px-4 py-3">
        <div className="flex-1"></div>
        <div className="flex-1 flex justify-center">
          <Logo />
        </div>
        <div className="flex-1 flex justify-end">
          {isAuthenticated ? (
            <button 
              onClick={() => setIsProfileModalOpen(true)}
              className="text-white hover:text-[#5CD2F4] transition-colors"
              aria-label="Open profile"
            >
              <User size={24} />
            </button>
          ) : (
            <Link to="/login" className="text-white hover:text-[#5CD2F4] transition-colors">
              <User size={24} />
            </Link>
          )}
        </div>
      </div>
      
      {/* Profile Modal */}
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
    </div>
  );
}; 